<template>
  <div class="fade-in">
    <div class="columns">
      <div class="column is-full">
        <h1 class="title is-pulled-left">Accounts</h1>

        <div class="is-pulled-left search-bar">
          <div class="field" style="vertical-align: bottom;">
            <p class="control has-icons-left">
              <input
                class="input search"
                type="search"
                v-model="query"
                placeholder="Search accounts"
                @search="doSearch"
              />
              <span class="icon is-small is-right">
                <i class="fa fa-search"></i>
              </span>
            </p>
          </div>
        </div>

        <div class="is-pulled-right text-right">
          <button
            v-if="$can('practice:delete|practice:manage')"
            class="button is-default is-small"
            @click="billingCsv()"
          >
            <i v-if="!billing.loading" class="fa fa-file-excel-o"></i>
            <i v-if="billing.loading" class="fa fa-refresh fa-spin"></i>
            Billing csv
          </button>

          <router-link
            :to="{ name: 'practice-create' }"
            class="button is-primary is-small ml1"
          >
            <i class="fa fa-plus"></i> Add Account
          </router-link>

          <button
            class="button is-default is-small ml1"
            @click="updateCss()"
            title="Update All CSS"
          >
            <i v-if="!updatingCss" class="fa fa-file-code-o"></i>
            <i v-if="updatingCss" class="fa fa-refresh fa-spin"></i>
          </button>
        </div>

        <div class="is-clearfix"></div>
      </div>
    </div>

    <table class="table is-striped is-responsive table-accounts">
      <thead>
        <tr>
          <th>Logo</th>
          <th>Name</th>
          <th>Timezone</th>
          <th>Subdomain</th>
          <th>Keywords</th>
          <th>Contact</th>
          <th style="width: 100px">Virtual Visit</th>
          <th class="has-text-right"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="!searchLoading"
          v-bind:key="practice.id"
          v-for="practice in practices"
        >
          <td @click="switchTo(practice)" class="pointer">
            <p class="image is-64x64">
              <img
                :src="practice.attributes.logo"
                alt="logo"
                title="Edit logo"
                v-if="!logoLoading(practice.id)"
              />
              <i
                class="fa fa-refresh fa-spin has-text-grey-light"
                v-if="logoLoading(practice.id)"
              ></i>
            </p>
          </td>
          <td @click="switchTo(practice)" class="pointer">
            {{ practice.attributes.name }}
          </td>
          <td>{{ practice.attributes.timezone }}</td>
          <td>
            <a :href="url(practice)" target="_blank">{{
              practice.attributes.subdomain
            }}</a>
          </td>
          <td>
            <ul class="keyword-list">
              <li
                v-for="keyword in practice.attributes.keywords"
                v-bind:key="keyword"
                class="pointer"
                @click="showEditKeyword(practice.id, keyword)"
              >
                {{ keyword }}
                <i class="fa fa-pencil has-text-light"></i>
              </li>
            </ul>
          </td>
          <td>
            {{ practice.attributes.address }}
            <br />
            {{ practice.attributes.phone }}
            <br />
            {{ practice.attributes.websiteUrl }}
          </td>
          <td class="has-text-centered">
            {{ practice.attributes.virtualVisitEnabled ? "Yes" : "No" }}
          </td>
          <td class="edit has-text-right">
            <router-link
              :to="{ name: 'practice-edit', params: { id: practice.id } }"
              title="Edit"
            >
              <i class="fa fa-pencil"></i>
            </router-link>
            <router-link
              :to="{ name: 'user-list', params: { id: practice.id } }"
              title="Users"
              class="ml1"
            >
              <i class="fa fa-users"></i>
            </router-link>
            <a
              href="#"
              @click.prevent="showInterviewSelector(practice)"
              class="ml1"
              title="Assign interview"
            >
              <i class="fa fa-file-text-o"></i>
            </a>
            <a
              href="#"
              @click.prevent="editLogo(practice.id)"
              class="ml1"
              title="Change logo"
            >
              <i class="fa fa-image"></i>
            </a>
            <a
              v-if="canDelete(practice)"
              href="#"
              v-on:click.prevent="practiceDelete(practice)"
              class="ml1"
              title="Delete"
            >
              <i class="fa fa-times"></i>
            </a>
          </td>
        </tr>

        <tr v-if="searchLoading">
          <td colspan="8">
            <i class="fa fa-refresh fa-spin"></i>
          </td>
        </tr>
      </tbody>

      <tfoot v-if="!searchLoading">
        <tr>
          <td colspan="8">
            <pager
              :current="currentPage"
              :total="pageCount"
              :onClick="doSearch"
            />
          </td>
        </tr>
      </tfoot>
    </table>

    <input type="file" class="hidden" v-on:change="uploadLogo" ref="logo" />

    <alert ref="alert" title="Confirm Delete" :onConfirm="onConfirm"
      >Are you sure you want to delete this practice?</alert
    >
    <vue-toastr ref="toastr"></vue-toastr>
    <interview-selector
      v-if="interviewSelectorFor"
      :practice="interviewSelectorFor"
      :onAssign="setInterview"
      @hide="hideInterviewSelector"
      :loadingId="interviewAssignLoadingId"
      :keywordError="interviewKeywordError"
    ></interview-selector>
    <keyword-edit
      v-if="keywordEdit.show"
      :practiceId="keywordEdit.practiceId"
      :keyword="keywordEdit.keyword"
      @hide="hideKeywordEdit"
    ></keyword-edit>

    <div v-if="billing.url">
      <iframe :src="billing.url" frameborder="0" scrolling="no"></iframe>
    </div>
  </div>
</template>

<script>
import PracticesService from "@/services/PracticesService";
import InterviewsService from "@/services/InterviewsService";
import alert from "@/components/alert";
import interviewSelector from "./InterviewSelector";
import keywordEdit from "./KeywordEdit";
import pager from "./Pager";

export default {
  name: "practice-list",

  components: {
    alert,
    interviewSelector,
    keywordEdit,
    pager
  },

  data() {
    return {
      updatingCss: false,
      practiceId: null,
      interviewSelectorFor: false,
      loadingLogoId: null,
      interviewAssignLoadingId: null,
      interviewKeywordError: "",
      billing: {
        loading: false,
        url: null
      },
      keywordEdit: {
        practiceId: null,
        keyword: null,
        show: false
      },
      query: ""
    };
  },

  computed: {
    practices() {
      return this.$store.getters["practice/sortedList"];
    },
    pages() {
      return this.$store.getters["practice/pagination"];
    },
    currentPage() {
      return this.$store.getters["practice/currentPage"];
    },
    pageCount() {
      return this.$store.getters["practice/pageCount"];
    },
    searchLoading() {
      return this.$store.getters["practice/searchLoading"];
    }
  },

  methods: {
    url(practice) {
      const baseDomain = process.env.VUE_APP_SCHEDULER_BASE_DOMAIN;
      const protocol = process.env.VUE_APP_URL_PROTOCOL;
      return `${protocol}://${practice.attributes.subdomain}.${baseDomain}`;
    },
    practiceDelete(practice) {
      this.$refs.alert.open();
      this.practiceId = practice.id;
    },
    onConfirm() {
      PracticesService.delete(this.practiceId)
        .then(() => {
          this.$store.dispatch("practice/remove", this.practiceId);
          this.$refs.toastr.s("The practice has been deleted.");
          this.practiceId = null;
        })
        .catch(() => {
          this.$refs.toastr.e("There was an error deleting the practice.");
          this.practiceId = null;
        });
    },
    editLogo(id) {
      this.practiceId = id;
      this.$refs.logo.click();
    },
    uploadLogo() {
      if (this.practiceId && this.$refs.logo.files) {
        this.loadingLogoId = this.practiceId;
        PracticesService.uploadLogo(
          this.practiceId,
          this.$refs.logo.files[0]
        ).then(() => {
          // clear input so change will fire if they choose the same image name again
          this.$refs.logo.value = null;
          this.practiceId = null;
          this.loadingLogoId = null;
        });
      }
    },
    canDelete(practice) {
      return (
        practice.attributes.subdomain !== "public" &&
        this.$can("practice:delete|practice:manage")
      );
    },
    showInterviewSelector(practice) {
      this.interviewKeywordError = "";
      this.interviewSelectorFor = practice;
    },

    setInterview(interviewId, keyword) {
      this.interviewAssignLoadingId = interviewId;
      const values = {
        practiceId: this.interviewSelectorFor.id,
        interviewId,
        keyword
      };
      PracticesService.initializeInterview(values)
        .then(() => {
          this.interviewAssignLoadingId = null;
          this.hideInterviewSelector();
          this.$refs.toastr.s("The interview has been assigned.");
        })
        .catch((e) => {
          this.interviewAssignLoadingId = null;
          let message = e
          if (e.response.data.message !== undefined && e.response.data.message == 'Errors::KeywordNotUnique') {
            message = "The keyword has already been taken.";
          } else {
            console.error("An Error: ", e.response)
          }
          this.interviewKeywordError = message;
        });
    },

    hideInterviewSelector() {
      this.interviewSelectorFor = false;
    },

    switchTo(practice) {
      window.bus.$emit("practice-switch", practice);
    },

    logoLoading(id) {
      return this.loadingLogoId === id;
    },
    updateCss() {
      this.updatingCss = true;
      this.$refs.toastr.s(
        "Updating CSS. This could take a while, so grab a drink."
      );
      PracticesService.updateCss().then(() => {
        this.$refs.toastr.s("Updating CSS. Done!");
        this.updatingCss = false;
      });
    },
    billingCsv() {
      this.billing.loading = true;
      PracticesService.billingCsv().then(data => {
        this.$refs.toastr.s("CSV Created.");
        this.billing.loading = false;
        this.billing.url = data.url;
      });
    },
    showEditKeyword(practiceId, keyword) {
      this.keywordEdit = {
        practiceId,
        keyword,
        show: true
      };
    },
    hideKeywordEdit() {
      this.keywordEdit.show = false;
    },
    doSearch(page = 1) {
      this.$store.dispatch("practice/setSearchLoading", true);
      this.$store.dispatch("practice/setSearchQuery", this.query);
      PracticesService.list(this.query, page).then(() => {
        this.$store.dispatch("practice/setSearchLoading", false);
      });
    }
  },

  mounted() {
    InterviewsService.getPool();
    InterviewsService.getTags();
    PracticesService.getTimezones();
  }
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: 1007px) {
  table.table-accounts {
    thead,
    tbody {
      tr {
        th,
        td,
        td.edit.has-text-right {
          text-align: center !important;
        }
      }
    }
  }
}
</style>
